import { useState, useEffect, useCallback, useId, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { ChatExchange } from '../../components/ChatExchange';
import { userAPI } from '../../UserAPI';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../../State';

import { Info } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface UseTestDialogProps {
    onOrder?: (marketplaceOffer: any) => void;
    /** Per offer: false for offers the customer already owns — they get a note instead of the order button. */
    canOrder?: (marketplaceOffer: any) => boolean;
    resourceSelected?: (resource: any) => void;
}

// Inner component to maintain stable state and prevent ChatExchange from unmounting
const TestDialogContent = ({
    isOpen,
    closeTestDialog,
    selectedMarketplaceOffer,
    conversation,
    isCreatingConversation,
    onOrder,
    canOrder,
    resourceSelected
}: {
    isOpen: boolean;
    closeTestDialog: () => void;
    selectedMarketplaceOffer: any;
    conversation: any;
    isCreatingConversation: boolean;
    onOrder?: (offer: any) => void;
    canOrder?: (offer: any) => boolean;
    resourceSelected?: (resource: any) => void;
}) => {
    const { t } = useTranslation();
    const [testMode, setTestMode] = useState<string>('toolOnly');
    const [demoMode, setDemoMode] = useState(false);
    const fieldId = useId();

    const testModeOptions = [
        {
            key: 'toolOnly',
            text: t('settingsPanel.agentPanel.testDialog.toolOnly'),
        },
        {
            key: 'withExisting',
            text: t('settingsPanel.agentPanel.testDialog.withExisting'),
        },
    ];

    const getExamplePrompts = (marketplaceOffer: any) => {
        if (!marketplaceOffer) return [];
        return marketplaceOffer.sampleRequests || [];
    };

    const handleResourceSelected = (resource: any) => {
        if (resourceSelected) {
            resourceSelected(resource);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) closeTestDialog();
            }}
        >
            {/*
              * Breit genug für eine Antwort, die wie eine Antwort aussieht: Ein Prompt-Werkzeug
              * liefert Tabellen (Ausschreibungsanalyse: Feld/Wert über die ganze Zeile), und in
              * 1000px stand davon je Zelle ein Wort. `min(96vw, 1600px)` lässt auf jedem Schirm
              * einen Rand stehen, statt bei 1600px auf kleineren Geräten überzulaufen.
              *
              * `flex` statt des Rasters aus `DialogContent`: Ein Rasterkind hat `min-width: auto`
              * und wächst mit seinem breitesten Inhalt aus dem Dialog heraus. Mit `min-w-0` und
              * `overflow-hidden` bleibt der Rahmen der Rahmen, und was breiter ist, rollt in sich.
              */}
            <DialogContent className="flex h-[90vh] max-h-[90vh] max-w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-[min(96vw,1600px)]">
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.agentPanel.testDialog.title')}</DialogTitle>
                </DialogHeader>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <div className="mb-4 flex flex-row flex-wrap items-center gap-5">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                className="peer"
                                id={`${fieldId}-demoMode`}
                                checked={demoMode}
                                onCheckedChange={(checked) => setDemoMode(checked === true)}
                                disabled={testMode !== 'toolOnly'}
                            />
                            <Label htmlFor={`${fieldId}-demoMode`}>
                                {t('settingsPanel.agentPanel.testDialog.demoMode')}
                            </Label>
                        </div>
                        <RadioGroup
                            className="flex flex-row flex-wrap gap-5"
                            value={testMode}
                            onValueChange={(value) => value && setTestMode(value)}
                        >
                            {testModeOptions.map((option) => (
                                <div key={option.key} className="flex items-center gap-2">
                                    <RadioGroupItem id={`${fieldId}-${option.key}`} value={option.key} />
                                    <Label htmlFor={`${fieldId}-${option.key}`}>{option.text}</Label>
                                </div>
                            ))}
                        </RadioGroup>
                    </div>
                    {isCreatingConversation && (
                        <div className="mb-4">
                            <Alert variant="info">
                                <Info aria-hidden className="h-4 w-4" />
                                <AlertDescription>
                                    {t('settingsPanel.agentPanel.creatingTestConversation')}
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}
                    {/*
                      * Was breiter ist als der Dialog, rollt in sich selbst — die Tabelle einer
                      * Analyse, ein langer Codeblock, ein Bild. Ohne das schiebt der breiteste
                      * Inhalt die ganze Unterhaltung nach rechts aus dem Fenster.
                      */}
                    <div className="mt-5 flex min-h-0 min-w-0 flex-1 flex-col break-words [&_img]:max-w-full [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto">
                        <ChatExchange
                            conversation={conversation}
                            showSuggestions={false}
                            showLogo={false}
                            fullWidth={true}
                            height="100%"
                            sampleRequests={getExamplePrompts(selectedMarketplaceOffer)}
                            toolsFilter={testMode === 'toolOnly' ?
                                {
                                    exclusiveToolsFilter: [selectedMarketplaceOffer?._id],
                                    toolVersion: selectedMarketplaceOffer,
                                    demo: demoMode
                                }
                                :
                                {
                                    offeredToolsAddedFilter: [selectedMarketplaceOffer?._id],
                                    toolVersion: selectedMarketplaceOffer,
                                    demo: demoMode
                                }}
                            resourceSelected={handleResourceSelected}
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={async () => await closeTestDialog()}>
                        {t('global.close')}
                    </Button>
                    {onOrder && (canOrder && !canOrder(selectedMarketplaceOffer)
                        ? <span className="inline-flex items-center px-3 text-sm text-muted-foreground">
                            {t('agentPanel.store.owned')}
                        </span>
                        : <Button onClick={async () => {
                            await closeTestDialog();
                            onOrder(selectedMarketplaceOffer);
                        }}>
                            {t('settingsPanel.agentPanel.order')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export function useTestDialog({ onOrder, canOrder, resourceSelected }: UseTestDialogProps = {}) {
    const [authentication] = useAtom(authenticationAtom) as any;
    const [isOpen, setIsOpen] = useState(false);
    const [selectedMarketplaceOffer, setSelectedMarketplaceOffer] = useState<any>(null);
    const [conversation, setConversation] = useState<any>({ messages: [] });
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);

    const openTestDialog = async (marketplaceOffer: any) => {
        setSelectedMarketplaceOffer(marketplaceOffer);
        setIsOpen(true);
        setIsCreatingConversation(true);

        try {
            const newConversation = await userAPI.createConversation(authentication.customer);
            setConversation(newConversation);
        } catch (error) {
            console.error('Failed to create test conversation:', error);
        } finally {
            setIsCreatingConversation(false);
        }
    };

    const closeTestDialog = useCallback(async () => {
        if (conversation && conversation._id) {
            try {
                await userAPI.deleteConversation(authentication.customer, conversation);
            } catch (error) {
                console.error('Failed to delete test conversation:', error);
            }
        }

        setIsOpen(false);
        setConversation({ messages: [] });
        setSelectedMarketplaceOffer(null);
        setIsCreatingConversation(false);
    }, [conversation, authentication.customer]);

    useEffect(() => {
        return () => {
            if (isOpen && conversation && conversation._id) {
                userAPI.deleteConversation(authentication.customer, conversation)
                    .catch((error) => console.error('Failed to delete test conversation on unmount:', error));
            }
        };
    }, [isOpen, conversation, authentication.customer]);

    // Return a stable JSX element rather than a component function. A function
    // wrapper changes identity on every state mutation (isOpen, conversation,
    // …), and `<TestDialog />` in the parent JSX would then be treated as a
    // new component type each time — causing radix's Dialog portal to be
    // unmounted/remounted mid-open. That manifested as two dialogs flashing,
    // with the first one closed/obfuscated by the second.
    const TestDialog = useMemo(() => (
        <TestDialogContent
            isOpen={isOpen}
            closeTestDialog={closeTestDialog}
            selectedMarketplaceOffer={selectedMarketplaceOffer}
            conversation={conversation}
            isCreatingConversation={isCreatingConversation}
            onOrder={onOrder}
            canOrder={canOrder}
            resourceSelected={resourceSelected}
        />
    ), [isOpen, closeTestDialog, selectedMarketplaceOffer, conversation, isCreatingConversation, onOrder, canOrder, resourceSelected]);

    return {
        TestDialog,
        openTestDialog,
        closeTestDialog
    };
}

